import { Button, Center, Group, Stack, Text, ThemeIcon } from '@mantine/core';
import { useDisclosure, useInterval } from '@mantine/hooks';
import { IconCheck } from '@tabler/icons-react';
import mixpanel from 'mixpanel-browser';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getToken, UserInfo, safeDecodeURIComponent, saveTokenInfo } from '@mygpt/app';
import { useLogin } from '@/hooks/useLogin';
import { useGPTStore } from '@/store';
import { getUrlHashParams } from '@/utils/helper';
import { TokenInfo } from '@/utils/interface';
import MailVerificationModal from './MailVerificationModal';

const Login = () => {
  const { t } = useTranslation();
  const { loginCallback } = useLogin();
  const { fetchUserInfo } = useGPTStore((state) => state);
  const { login } = useLogin();
  const [opened, { close, open }] = useDisclosure(false);

  const [text, setText] = useState('Be logging in ...');

  // 按钮数秒
  const MAX_SEND_INTERVAL = 60;
  const [emailVerificationSeconds, setEmailVerificationSeconds] = useState(MAX_SEND_INTERVAL);
  const emailVerificationInterval = useInterval(() => setEmailVerificationSeconds((s) => s - 1), 1000);
  const isEmailVerification = useMemo(() => {
    return emailVerificationSeconds < MAX_SEND_INTERVAL;
  }, [emailVerificationSeconds]);
  useEffect(() => {
    if (emailVerificationSeconds <= 1) {
      emailVerificationInterval.stop();
      setEmailVerificationSeconds(MAX_SEND_INTERVAL);
    }
  }, [emailVerificationSeconds]);

  const isEmailVerify = useMemo(() => {
    return text.indexOf('Please verify your email before logging in') !== -1;
  }, [text]);

  const isEmailVerificationSuccess = useMemo(() => {
    return text.indexOf('Your email was verified') !== -1;
  }, [text]);

  const initUrlToken = async () => {
    const urlParams = getUrlHashParams();
    let { access_token, expires_in, refresh_token, token_type } = urlParams;
    const { code, error, error_description, login_type, message, state } = urlParams;

    //方便其他平台测试
    let redirectUrl = '';
    if (state && state !== 'None') {
      const redirectUri = atob(safeDecodeURIComponent(state));
      const url = new URL(redirectUri);
      if (window.location.origin !== url.origin) {
        window.location.href = `${url.origin}/login${window.location.search}`;
        return;
      }
      redirectUrl = redirectUri;
    }

    /**
     * 第一版 url回调urltoken 直接登录
     * 第二版 url回调code 请求获取token和refresh token
     */

    if (code) {
      const res = await getToken({ code });
      const data: TokenInfo = res.data;
      access_token = data.access_token;
      expires_in = data.expires_in;
      refresh_token = data.refresh_token;
      token_type = data.token_type;
    }

    saveTokenInfo({ access_token, refresh_token, expires_in, token_type, scope: '' });

    return new Promise((resolve, reject) => {
      /** 其他 */

      if (login_type === 'email_verify') {
        reject({ errorDescription: message || 'Your email was verified' });
        return;
      }

      if (error && error_description) {
        reject({ error, errorDescription: error_description });
        // 有错误就不跳转了
        return;
      } else if (access_token && token_type && expires_in) {
        resolve({});
      }

      // state参数  目前存了redirect url地址
      if (redirectUrl) {
        window.location.href = redirectUrl;
      }
    });
  };

  useLayoutEffect(() => {
    initUrlToken()
      .then((data) => {
        if (!data) return;
        fetchUserInfo().then((userInfo: UserInfo) => {
          loginCallback();
          mixpanel.track('login', { email: userInfo.email });
        });
      })
      .catch(({ error, errorDescription }) => {
        setText(errorDescription);
      });
  }, []);

  return (
    <>
      <Center className="pt-32">
        {isEmailVerificationSuccess ? (
          <Stack align="center" gap={30}>
            <ThemeIcon color="teal.6" radius={65} size={65}>
              <IconCheck size={40} />
            </ThemeIcon>
            <Button
              onClick={() => {
                login();
              }}
            >
              {t('signin_now')}
            </Button>
          </Stack>
        ) : (
          <Text size={'xl'} ta="center" className="max-w-screen-md whitespace-pre-line mx-20">
            {isEmailVerify ? t('errors.email_verify') : text}
          </Text>
        )}
      </Center>
      {isEmailVerify && (
        <Group className="w-full" justify="center" mt={30} gap={'xl'}>
          <Button
            variant="outline"
            onClick={() => {
              login();
            }}
          >
            {t('signin')}
          </Button>
          {/* <Button
            variant="gradient"
            gradient={{ from: '#6d28d9', to: '#be81ff' }}
            onClick={() => {
              logout(getLoginUrl());
            }}
          >
            {t('re_register')}
          </Button> */}
          <Button
            disabled={isEmailVerification}
            variant="gradient"
            gradient={{ from: '#6d28d9', to: '#be81ff' }}
            onClick={open}
          >
            <span>{t('send_again')}</span>
            {isEmailVerification && <span className="ml-2">{emailVerificationSeconds}</span>}
          </Button>
        </Group>
      )}
      <MailVerificationModal
        opened={opened}
        onClose={close}
        onConfirm={() => {
          emailVerificationInterval.start();
        }}
      />
    </>
  );
};

export default Login;
